import { type ReactElement } from 'react';
import NoIndex from '../components/NoIndex';

/**
 * Datenschutzerklärung nach Art. 13 DSGVO.
 *
 * Angepasst an den tatsächlichen Stack von Saldify:
 *  - Google Analytics (nur nach Einwilligung via Consent-Banner / Consent Mode v2)
 *  - Google Fonts (aktuell von Google-Servern geladen)
 *  - Stripe (Zahlungsabwicklung Abo)
 *  - FinTS-Banking (Kontoabruf, nur im eingeloggten Bereich)
 *  - Photon/OpenStreetMap (Adress-Autovervollständigung bei Nachweisen)
 *  - Google Gemini (OCR/Belegerkennung im eingeloggten Bereich)
 *
 * HINWEIS: Dieser Text ist ein an den Stack angepasstes, sorgfältig
 * formuliertes Grundgerüst – er ersetzt keine anwaltliche Prüfung.
 */
export default function Datenschutz(): ReactElement {
  return (
    <div className="pt-24 pb-16 lg:pt-32 lg:pb-24">
      <NoIndex />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-8">Datenschutzerklärung</h1>

        <div className="max-w-none space-y-8 text-secondary-700 leading-relaxed">
          {/* 1. Verantwortlicher */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              1. Verantwortlicher
            </h2>
            <p>
              Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:
            </p>
            <p className="mt-2">
              CuiveMedia, Inhaber Brian Brelöer
              <br />
              Siriusstraße 2, 12524 Berlin, Deutschland
              <br />
              Telefon: (030) 89 39 91 47
              <br />
              E-Mail:{' '}
              <a href="mailto:info@saldify.de" className="text-primary-600 hover:underline">
                info@saldify.de
              </a>
            </p>
          </section>

          {/* 2. Ihre Rechte */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              2. Ihre Rechte als betroffene Person
            </h2>
            <p>Sie haben gegenüber uns folgende Rechte hinsichtlich Sie betreffender personenbezogener Daten:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
              <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
              <li>Recht auf Löschung (Art. 17 DSGVO)</li>
              <li>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
              <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Recht auf Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
              <li>Recht auf Widerruf einer erteilten Einwilligung (Art. 7 Abs. 3 DSGVO)</li>
            </ul>
            <p className="mt-2">
              Zudem haben Sie das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über
              die Verarbeitung Ihrer personenbezogenen Daten zu beschweren. Zuständig ist
              die Berliner Beauftragte für Datenschutz und Informationsfreiheit.
            </p>
          </section>

          {/* 3. Server-Logfiles */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              3. Bereitstellung der Website und Server-Logfiles
            </h2>
            <p>
              Beim Aufrufen unserer Website werden durch den Browser automatisch
              Informationen an den Server übermittelt und temporär in sogenannten
              Logfiles gespeichert: IP-Adresse, Datum und Uhrzeit der Anfrage,
              aufgerufene Seite, übertragene Datenmenge, verwendeter Browser und
              Betriebssystem sowie der zuvor besuchte Verweis (Referrer).
            </p>
            <p className="mt-2">
              Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse
              folgt aus dem technisch fehlerfreien Betrieb und der Sicherheit der Website.
            </p>
          </section>

          {/* 4. Cookies & Einwilligung */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              4. Cookies und Einwilligungsverwaltung
            </h2>
            <p>
              Wir verwenden technisch notwendige Cookies, die für den Betrieb der Website
              und des Nutzerkontos erforderlich sind (Rechtsgrundlage Art. 6 Abs. 1 lit. f
              DSGVO bzw. § 25 Abs. 2 TDDDG).
            </p>
            <p className="mt-2">
              Nicht notwendige Cookies und Dienste (insbesondere die Statistik-/Analyse-
              Dienste, siehe unten) setzen wir nur ein, sofern Sie über unseren
              Cookie-Banner Ihre Einwilligung erteilt haben (§ 25 Abs. 1 TDDDG,
              Art. 6 Abs. 1 lit. a DSGVO). Sie können Ihre Einwilligung jederzeit mit
              Wirkung für die Zukunft über den Link „Cookie-Einstellungen" im Seitenfuß
              widerrufen oder anpassen.
            </p>
          </section>

          {/* 5. Google Analytics */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              5. Google Analytics
            </h2>
            <p>
              Sofern Sie eingewilligt haben, nutzen wir Google Analytics, einen
              Webanalysedienst der Google Ireland Limited, Gordon House, Barrow Street,
              Dublin 4, Irland. Google Analytics verwendet Cookies, die eine Analyse der
              Benutzung der Website ermöglichen. Wir setzen den Google Consent Mode v2 ein;
              ohne Ihre Einwilligung werden keine Analyse-Cookies gesetzt und keine
              personenbezogenen Messdaten verarbeitet. Die IP-Anonymisierung ist aktiviert.
            </p>
            <p className="mt-2">
              Rechtsgrundlage ist Ihre Einwilligung (Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1
              TDDDG). Im Rahmen der Nutzung kann es zu einer Übermittlung von Daten an
              Google-Server, ggf. in die USA, kommen; Grundlage hierfür sind die
              EU-Standardvertragsklauseln. Mit Google besteht ein Vertrag zur
              Auftragsverarbeitung. Sie können Ihre Einwilligung jederzeit widerrufen.
            </p>
          </section>

          {/* 6. Google Fonts */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              6. Google Fonts
            </h2>
            <p>
              Zur einheitlichen Darstellung von Schriftarten nutzen wir Google Fonts
              (Google Ireland Limited). Beim Aufruf einer Seite lädt Ihr Browser die
              benötigten Schriftarten; dabei wird Ihre IP-Adresse an Google übermittelt.
              Rechtsgrundlage ist unser berechtigtes Interesse an einer einheitlichen
              Darstellung (Art. 6 Abs. 1 lit. f DSGVO).
            </p>
          </section>

          {/* 7. Stripe */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              7. Zahlungsabwicklung über Stripe
            </h2>
            <p>
              Für die Abwicklung kostenpflichtiger Abonnements nutzen wir den
              Zahlungsdienstleister Stripe Payments Europe, Ltd., 1 Grand Canal Street
              Lower, Grand Canal Dock, Dublin, Irland. Bei einem Kauf werden die zur
              Zahlung erforderlichen Daten (z. B. Name, E-Mail-Adresse, Zahlungsdaten wie
              Kreditkarte, SEPA-Lastschrift, Wallet) direkt bei Stripe verarbeitet. Die
              vollständigen Zahlungsdaten werden ausschließlich bei Stripe gespeichert; wir
              erhalten diese nicht.
            </p>
            <p className="mt-2">
              Rechtsgrundlage ist die Erfüllung des Vertrags (Art. 6 Abs. 1 lit. b DSGVO).
              Weitere Informationen finden Sie in der Datenschutzerklärung von Stripe.
            </p>
          </section>

          {/* 8. Banking / FinTS */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              8. Kontoanbindung (Banking / FinTS)
            </h2>
            <p>
              Im eingeloggten Bereich können Sie Ihr Bankkonto über das FinTS-Verfahren
              anbinden, um Umsätze automatisch abzurufen. Hierzu verarbeiten wir die von
              Ihnen eingegebenen Zugangsdaten und die abgerufenen Kontoumsätze
              ausschließlich zum Zweck der Buchhaltung. Die Kommunikation erfolgt direkt
              mit Ihrer Bank. Rechtsgrundlage ist die Erfüllung des Vertrags
              (Art. 6 Abs. 1 lit. b DSGVO).
            </p>
          </section>

          {/* 9. Beleg-Erkennung (OCR) */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              9. Automatische Belegerkennung (OCR)
            </h2>
            <p>
              Zur automatischen Erkennung von Belegdaten setzen wir einen KI-gestützten
              Dienst (Google Gemini, Google Ireland Limited) ein. Die hochgeladenen Belege
              werden zum Zweck der Texterkennung verarbeitet. Rechtsgrundlage ist die
              Erfüllung des Vertrags (Art. 6 Abs. 1 lit. b DSGVO).
            </p>
          </section>

          {/* 10. Adress-Autovervollständigung */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              10. Adress-Autovervollständigung
            </h2>
            <p>
              Bei der Erfassung von Reisekosten/Nachweisen bieten wir eine
              Adress-Autovervollständigung über den Dienst Photon (auf Basis von
              OpenStreetMap-Daten) an. Dabei wird Ihre Eingabe zur Ermittlung von
              Adressvorschlägen an den Dienst übermittelt. Rechtsgrundlage ist unser
              berechtigtes Interesse an einer komfortablen Eingabe
              (Art. 6 Abs. 1 lit. f DSGVO).
            </p>
          </section>

          {/* 11. Nutzerkonto */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              11. Registrierung und Nutzerkonto
            </h2>
            <p>
              Für die Nutzung von Saldify ist ein Nutzerkonto erforderlich. Dabei
              verarbeiten wir die von Ihnen angegebenen Daten (z. B. Name, E-Mail-Adresse,
              Unternehmensdaten) sowie die im Rahmen der Nutzung anfallenden
              Buchhaltungsdaten zur Bereitstellung des Dienstes. Rechtsgrundlage ist die
              Erfüllung des Vertrags (Art. 6 Abs. 1 lit. b DSGVO). Gesetzliche
              Aufbewahrungsfristen (insbesondere handels- und steuerrechtlich) bleiben
              unberührt.
            </p>
          </section>

          {/* 12. Speicherdauer */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              12. Speicherdauer
            </h2>
            <p>
              Wir verarbeiten und speichern personenbezogene Daten nur so lange, wie es für
              die Erreichung des jeweiligen Zwecks erforderlich ist oder gesetzliche
              Aufbewahrungsfristen (z. B. § 147 AO, § 257 HGB) dies vorschreiben.
              Anschließend werden die Daten gelöscht.
            </p>
          </section>

          <p className="text-sm text-secondary-500 pt-4 border-t border-secondary-200">
            Stand: Diese Datenschutzerklärung wird bei Änderungen unserer Dienste
            aktualisiert.
          </p>
        </div>
      </div>
    </div>
  );
}
